/**
 * Single source of truth for the locale set the platform ships.
 *
 * English is the default locale and is served at the ROOT of the site (no path
 * prefix). German, French and Italian are served below locale prefixes.
 *
 * Adding a third locale later is a localized, config-only change: add its code
 * here, add its slugs to {@link file://./routes.ts} and its strings to a new
 * dictionary in `dictionaries/`. Nothing in the routing/SEO/nav layers hard
 * codes a specific locale, so the rest of the app picks the new locale up for
 * free.
 */

export const LOCALES = ['en', 'de', 'fr', 'it'] as const

export type Locale = (typeof LOCALES)[number]

/** The locale served at the site root with no URL prefix. */
export const DEFAULT_LOCALE: Locale = 'en'

/**
 * BCP-47 language tags used in `<html lang>`, hreflang alternates, Open Graph
 * `og:locale`, JSON-LD `inLanguage`, the Convex `newsletterSubscriptions.locale`
 * column and `Intl` date formatting. Keep these in lockstep with {@link LOCALES}.
 */
export const LOCALE_HTML_LANG: Record<Locale, string> = {
  en: 'en',
  de: 'de-CH',
  fr: 'fr',
  it: 'it',
}

/** Open Graph locale tokens (`og:locale`). */
export const LOCALE_OG: Record<Locale, string> = {
  en: 'en_US',
  de: 'de_CH',
  fr: 'fr_FR',
  it: 'it_IT',
}

/**
 * The BCP-47 tag stored on the Convex `newsletterSubscriptions.locale` column.
 * The DE locale keeps its historical `de-CH` value so existing rows stay valid;
 * EN newsletter sign-ups store `en`.
 */
export const LOCALE_DATA_TAG: Record<Locale, 'en' | 'de-CH' | 'fr' | 'it'> = {
  en: 'en',
  de: 'de-CH',
  fr: 'fr',
  it: 'it',
}

/** `Intl` locale used for date / number formatting per UI locale. */
export const LOCALE_INTL: Record<Locale, string> = {
  en: 'en-US',
  de: 'de-CH',
  fr: 'fr-FR',
  it: 'it-IT',
}

/** The path prefix a locale lives under (empty for the default/root locale). */
export function localePrefix(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? '' : `/${locale}`
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/**
 * Read the leading locale segment from a pathname.
 *
 * Returns the matched non-default locale plus the remaining path, or the
 * default locale with the path untouched when there is no locale prefix.
 *
 * @example
 *   detectLocaleFromPath('/de/kurse') // { locale: 'de', rest: '/kurse' }
 *   detectLocaleFromPath('/courses')  // { locale: 'en', rest: '/courses' }
 *   detectLocaleFromPath('/de')       // { locale: 'de', rest: '/' }
 */
export function detectLocaleFromPath(pathname: string): {
  locale: Locale
  rest: string
} {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]
  if (first && first !== DEFAULT_LOCALE && isLocale(first)) {
    const rest = `/${segments.slice(1).join('/')}`
    return { locale: first, rest: rest === '/' ? '/' : rest.replace(/\/$/u, '') }
  }
  return { locale: DEFAULT_LOCALE, rest: pathname }
}
